import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import {
  formatMedicationTimingPatternContext,
  formatMedicationTimingPatternCounts,
  formatMedicationTimingPatternEvidenceTime,
  formatMedicationTimingPatternEvidenceValue,
  formatMedicationTimingPatternRowSummary,
  getMedicationTimingPatternCategoryLabel,
  getMedicationTimingPatternEvidenceSections,
  sortMedicationTimingPatterns,
} from './utils/medicationTimelinePatternPresentation';

const TEXT_PRIMARY = '#1F2937';
const TEXT_SECONDARY = '#4B5563';
const TEXT_MUTED = '#6B7280';
const WARNING_TEXT = 'This is a timing-based pattern, not proof that the medication caused the change.';

const scrollToTimelineEntry = (entryId = '') => {
  const target = document.getElementById(`timeline-entry-${entryId}`);
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'nearest' });
};

const PatternEvidenceItem = ({ patternCategory, evidence = {} }) => {
  const entryId = String(evidence.entryId || '').trim();
  const evidenceTime = formatMedicationTimingPatternEvidenceTime(evidence.occurredAt);
  const value = formatMedicationTimingPatternEvidenceValue(patternCategory, evidence);
  const label = [evidenceTime, getMedicationTimingPatternCategoryLabel(patternCategory), value].filter(Boolean).join(' · ');

  return (
    <Button
      onClick={() => scrollToTimelineEntry(entryId)}
      fullWidth
      disableElevation
      variant="text"
      sx={{
        textTransform: 'none',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        px: 0.7,
        py: 0.65,
        minHeight: 0,
        borderRadius: '12px',
        bgcolor: 'transparent',
        color: TEXT_PRIMARY,
        border: `1px solid ${alpha(colors.semantic.line, 0.82)}`,
        '&:hover': {
          bgcolor: alpha(colors.semantic.sageSoft, 0.6),
          borderColor: alpha(colors.semantic.sageDeep, 0.22),
        },
      }}
      aria-label={`Jump to timeline entry ${entryId}`}
    >
      <Stack spacing={0.2} sx={{ alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
        <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.24, fontWeight: 700, color: TEXT_PRIMARY, textAlign: 'left' }}>
          {label}
        </Typography>
        {entryId ? (
          <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.16, fontWeight: 600, color: TEXT_MUTED, textAlign: 'left' }}>
            Entry {entryId}
          </Typography>
        ) : null}
      </Stack>
    </Button>
  );
};

const PatternEvidenceSection = ({ pattern }) => {
  const sections = getMedicationTimingPatternEvidenceSections(pattern);

  return (
    <Stack spacing={1} sx={{ mt: 0.35 }}>
      {sections.map((section) => (
        <Box key={section.key}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: TEXT_MUTED, mb: 0.4 }}>
            {section.label}
          </Typography>
          <Stack spacing={0.35}>
            {section.items.length ? section.items.map((item) => (
              <PatternEvidenceItem
                key={item.entryId}
                patternCategory={pattern.patternCategory}
                evidence={item}
              />
            )) : (
              <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.28, color: TEXT_MUTED }}>
                No supporting entries.
              </Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const MedicationTimingPatternCard = ({
  medicationName = '',
  medicationChangeType = '',
  effectiveAt = '',
  patterns = [],
}) => {
  const sortedPatterns = React.useMemo(() => sortMedicationTimingPatterns(patterns), [patterns]);
  const [evidenceOpen, setEvidenceOpen] = React.useState(false);

  if (!sortedPatterns.length) {
    return null;
  }

  const contextText = formatMedicationTimingPatternContext({
    medicationChangeType,
    medicationName,
    effectiveAt,
  });

  return (
    <Box
      sx={{
        mt: 0.95,
        ml: { xs: 0.05, sm: 0.35 },
        pl: { xs: 0.65, sm: 0.8 },
        borderLeft: `2px solid ${alpha(colors.semantic.sageDeep, 0.18)}`,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderRadius: '16px',
          borderColor: alpha(colors.semantic.sageDeep, 0.16),
          bgcolor: alpha(colors.semantic.sageSoft, 0.38),
          boxShadow: 'none',
        }}
      >
        <Box sx={{ px: { xs: 1.0, sm: 1.15 }, py: { xs: 0.9, sm: 1.0 } }}>
          <Stack spacing={0.8}>
            <Stack spacing={0.45}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.semantic.sageDeep }}>
                  Possible timing patterns
                </Typography>
                <Chip
                  label={getMedicationTimingPatternCategoryLabel(sortedPatterns[0]?.patternCategory || '')}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    color: colors.semantic.sageDeep,
                    bgcolor: alpha(colors.semantic.sageDeep, 0.1),
                    border: `1px solid ${alpha(colors.semantic.sageDeep, 0.14)}`,
                  }}
                />
              </Stack>

              {contextText ? (
                <Typography sx={{ fontSize: '0.86rem', lineHeight: 1.35, fontWeight: 700, color: TEXT_PRIMARY }}>
                  {contextText}
                </Typography>
              ) : null}
            </Stack>

            <Stack spacing={0.55} sx={{ pl: { xs: 0, sm: 0.05 } }}>
              {sortedPatterns.map((pattern) => (
                <Box
                  key={pattern.id}
                  sx={{
                    p: { xs: 0.75, sm: 0.8 },
                    borderRadius: '14px',
                    bgcolor: alpha('#FFFFFF', 0.8),
                    border: `1px solid ${alpha(colors.semantic.line, 0.82)}`,
                  }}
                >
                  <Stack spacing={0.35}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.22, fontWeight: 800, color: TEXT_PRIMARY }}>
                        {getMedicationTimingPatternCategoryLabel(pattern.patternCategory)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', lineHeight: 1.2, fontWeight: 700, color: TEXT_MUTED }}>
                        {formatMedicationTimingPatternCounts(pattern)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.32, color: TEXT_SECONDARY }}>
                      {formatMedicationTimingPatternRowSummary(pattern)}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Typography sx={{ fontSize: '0.77rem', lineHeight: 1.35, color: TEXT_SECONDARY }}>
              {WARNING_TEXT}
            </Typography>

            <Box>
              <Button
                onClick={() => setEvidenceOpen((current) => !current)}
                variant="text"
                size="small"
                endIcon={evidenceOpen ? <ExpandMoreOutlinedIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  color: colors.semantic.sageDeep,
                  px: 0,
                  py: 0,
                  minHeight: 0,
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                View supporting entries
              </Button>

              <Collapse in={evidenceOpen} timeout="auto" unmountOnExit>
                <Divider sx={{ my: 1, borderColor: alpha(colors.semantic.line, 0.72) }} />
                <Stack spacing={1.2}>
                  {sortedPatterns.map((pattern) => (
                    <Box key={`${pattern.id}-evidence`}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: TEXT_MUTED, mb: 0.5 }}>
                        {getMedicationTimingPatternCategoryLabel(pattern.patternCategory)} evidence
                      </Typography>
                      <PatternEvidenceSection pattern={pattern} />
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default React.memo(MedicationTimingPatternCard);
